import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { ticketService, locationService, ticketCategoryService } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiGrid, FiList, FiSearch } from 'react-icons/fi';
import './Tickets.css';

const Tickets = () => {
  const [tickets, setTickets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' | 'list'
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'active' | 'inactive'
  const [showModal, setShowModal] = useState(false);
  const [editingTicket, setEditingTicket] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    category: '',
    description: '',
    base_price: '',
    location: '',
    is_active: true,
    is_event: false,
    event_from: '',
    event_to: '',
  });
  const [eventThumbnailFile, setEventThumbnailFile] = useState(null);
  const [eventBannerFile, setEventBannerFile] = useState(null);

  const toList = (data) => (Array.isArray(data) ? data : (data?.results ?? []));

  /** All categories for the form, ordered (include inactive so the list is never empty by mistake). */
  const categoryOptions = useMemo(() => {
    return categories
      .slice()
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [categories]);

  const fetchCategoriesOnly = useCallback(async () => {
    try {
      const res = await ticketCategoryService.getAll();
      setCategories(toList(res.data));
    } catch (e) {
      console.error('Failed to load ticket categories:', e);
    }
  }, []);

  const fetchData = async () => {
    try {
      const [ticketsRes, locationsRes, categoriesRes] = await Promise.allSettled([
        ticketService.getAll(),
        locationService.getAll(),
        ticketCategoryService.getAll(),
      ]);
      setTickets(
        ticketsRes.status === 'fulfilled' ? toList(ticketsRes.value.data) : []
      );
      setLocations(
        locationsRes.status === 'fulfilled' ? toList(locationsRes.value.data) : []
      );
      setCategories(
        categoriesRes.status === 'fulfilled' ? toList(categoriesRes.value.data) : []
      );
      if (ticketsRes.status === 'rejected') {
        console.error('Tickets list failed:', ticketsRes.reason);
      }
      if (categoriesRes.status === 'rejected') {
        console.error('Ticket categories failed:', categoriesRes.reason);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      setTickets([]);
      setLocations([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /** When opening Add/Edit, reload categories so the dropdown is never empty if tickets API failed earlier. */
  useEffect(() => {
    if (showModal) {
      fetchCategoriesOnly();
    }
  }, [showModal, fetchCategoriesOnly]);

  const buildTicketFormData = () => {
    const fd = new FormData();
    fd.append('name', formData.name);
    fd.append('category', formData.category);
    fd.append('description', formData.description || '');
    fd.append('base_price', String(formData.base_price));
    fd.append('is_active', formData.is_active ? 'true' : 'false');
    fd.append('is_event', formData.is_event ? 'true' : 'false');
    if (formData.is_event) {
      fd.append(
        'event_date_range',
        JSON.stringify({ from: formData.event_from, to: formData.event_to })
      );
    }
    /* Do not send event_date_range for normal tickets — empty string breaks JSONField ("Value must be valid JSON"). */
    if (eventThumbnailFile) fd.append('event_thumbnail', eventThumbnailFile);
    if (eventBannerFile) fd.append('event_banner', eventBannerFile);
    if (!editingTicket && formData.location) {
      fd.append('location', String(formData.location));
    }
    return fd;
  };

  const handleTicketSubmit = async (e) => {
    e.preventDefault();
    if (formData.is_event) {
      if (!formData.event_from || !formData.event_to) {
        alert('Please set both From date and End date for event tickets.');
        return;
      }
      const thumbOk = eventThumbnailFile || (editingTicket && editingTicket.event_thumbnail);
      const bannerOk = eventBannerFile || (editingTicket && editingTicket.event_banner);
      if (!thumbOk || !bannerOk) {
        alert('Event tickets require a thumbnail and a banner image (upload files or keep existing).');
        return;
      }
    }
    try {
      const fd = buildTicketFormData();
      if (editingTicket) {
        await ticketService.update(editingTicket.id, fd);
      } else {
        await ticketService.create(fd);
      }
      setShowModal(false);
      setEditingTicket(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error saving ticket:', error);
      const d = error.response?.data;
      let msg =
        d && typeof d === 'object' && d.detail != null
          ? (Array.isArray(d.detail) ? d.detail.join(' ') : String(d.detail))
          : d && typeof d === 'string'
            ? d
            : d
              ? JSON.stringify(d)
              : '';
      alert(msg ? `Error saving ticket: ${msg}` : 'Error saving ticket');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      category: '',
      description: '',
      base_price: '',
      location: '',
      is_active: true,
      is_event: false,
      event_from: '',
      event_to: '',
    });
    setEventThumbnailFile(null);
    setEventBannerFile(null);
  };

  const openEditTicket = (ticket) => {
    setEditingTicket(ticket);
    setFormData({
      ...ticket,
      base_price: ticket.base_price != null ? String(ticket.base_price) : '',
      location: '',
      is_event: !!ticket.is_event,
      event_from: ticket.event_date_range?.from || '',
      event_to: ticket.event_date_range?.to || '',
    });
    setEventThumbnailFile(null);
    setEventBannerFile(null);
    setShowModal(true);
  };

  const getCategoryName = (code) => {
    const cat = categories.find((c) => c.code === code);
    return cat ? cat.name : code;
  };

  const filteredTickets = useMemo(() => {
    let list = tickets;
    const q = (searchQuery || '').trim().toLowerCase();
    if (q) {
      list = list.filter((t) => {
        const nameMatch = t.name && t.name.toLowerCase().includes(q);
        const descMatch = t.description && t.description.toLowerCase().includes(q);
        const catName = getCategoryName(t.category);
        const catMatch = catName && catName.toLowerCase().includes(q);
        return nameMatch || descMatch || catMatch;
      });
    }
    if (statusFilter === 'active') list = list.filter((t) => t.is_active === true);
    if (statusFilter === 'inactive') list = list.filter((t) => t.is_active === false);
    return list;
  }, [tickets, searchQuery, statusFilter, categories]);

  if (loading) {
    return <div className="loading">Loading tickets...</div>;
  }

  return (
    <div className="tickets-page">
      <div className="page-header">
        <h1>Tickets Management</h1>
        <div className="page-header-actions">
          <div className="view-toggle">
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'cards' ? 'active' : ''}`}
              onClick={() => setViewMode('cards')}
              title="Card view"
            >
              <FiGrid />
              <span>Cards</span>
            </button>
            <button
              type="button"
              className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List view"
            >
              <FiList />
              <span>List</span>
            </button>
          </div>
          <button type="button" className="btn-primary" onClick={() => { setShowModal(true); setEditingTicket(null); resetForm(); }}>
            <FiPlus />
            Add Ticket Type
          </button>
        </div>
      </div>

      <div className="tickets-toolbar">
        <div className="search-wrap">
          <FiSearch className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search by ticket name, category or description"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-wrap">
          <label htmlFor="tickets-status-filter" className="filter-label">Status</label>
          <select
            id="tickets-status-filter"
            className="filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="tickets-list-wrap">
          <table className="tickets-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Category</th>
                <th>Description</th>
                <th>Base Price</th>
                <th>Type</th>
                <th>Status</th>
                <th className="th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan="7" className="td-empty">
                    {tickets.length === 0
                      ? 'No ticket types yet. Add one to get started.'
                      : 'No ticket types match your search or filter.'}
                  </td>
                </tr>
              ) : filteredTickets.map((ticket) => (
                <tr key={ticket.id}>
                  <td className="td-name">{ticket.name}</td>
                  <td>{getCategoryName(ticket.category)}</td>
                  <td className="td-desc">{ticket.description || '—'}</td>
                  <td className="td-price">₹ {parseFloat(ticket.base_price).toFixed(2)}</td>
                  <td>
                    {ticket.is_event ? (
                      <span className="badge badge-event">Event</span>
                    ) : (
                      <span className="badge badge-standard">Standard</span>
                    )}
                  </td>
                  <td>
                    <span className={`badge ${ticket.is_active ? 'active' : 'inactive'}`}>
                      {ticket.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="td-actions">
                    <button type="button" className="btn-edit" onClick={() => openEditTicket(ticket)}>
                      <FiEdit2 />
                    </button>
                    <button
                      type="button"
                      className="btn-delete"
                      onClick={async () => {
                        if (window.confirm('Are you sure?')) {
                          try {
                            await ticketService.delete(ticket.id);
                            fetchData();
                          } catch (error) {
                            alert('Error deleting ticket');
                          }
                        }
                      }}
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
      <div className="tickets-list">
        {filteredTickets.map((ticket) => (
          <div key={ticket.id} className="ticket-card">
            <div className="ticket-info">
              <h3>{ticket.name}</h3>
              <p className="ticket-category">{getCategoryName(ticket.category)}</p>
              <p className="ticket-description">{ticket.description}</p>
              <p className="ticket-price">Base Price: ₹ {parseFloat(ticket.base_price).toFixed(2)}</p>
              {ticket.is_event ? (
                <span className="badge badge-event" style={{ marginRight: 8 }}>Event</span>
              ) : null}
              <span className={`badge ${ticket.is_active ? 'active' : 'inactive'}`}>
                {ticket.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="ticket-actions">
              <button type="button" className="btn-edit" onClick={() => openEditTicket(ticket)}>
                <FiEdit2 />
              </button>
              <button className="btn-delete" onClick={async () => {
                if (window.confirm('Are you sure?')) {
                  try {
                    await ticketService.delete(ticket.id);
                    fetchData();
                  } catch (error) {
                    alert('Error deleting ticket');
                  }
                }
              }}>
                <FiTrash2 />
              </button>
            </div>
          </div>
        ))}
      </div>
      )}

      {filteredTickets.length === 0 && !loading && (
        <p className="no-data">
          {tickets.length === 0
            ? 'No ticket types yet. Add one to get started.'
            : 'No ticket types match your search or filter.'}
        </p>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => { setShowModal(false); setEditingTicket(null); resetForm(); }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editingTicket ? 'Edit Ticket Type' : 'Add Ticket Type'}</h2>
            <form onSubmit={handleTicketSubmit}>
              <div className="form-group">
                <label>Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  required
                >
                  <option value="">Select category</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat.id} value={cat.code}>
                      {cat.name}
                      {cat.is_active === false || cat.is_active === 0 ? ' (inactive)' : ''}
                    </option>
                  ))}
                </select>
                {categoryOptions.length === 0 ? (
                  <p className="form-hint form-hint--warn">
                    No categories loaded. Open the Categories page to confirm they exist, or check that GET /api/ticket-categories/ returns data (admin token required).
                  </p>
                ) : null}
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Base Price *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.base_price}
                  onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                  required
                />
              </div>
              {!editingTicket && (
                <div className="form-group">
                  <label>Location *</label>
                  <select
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    required
                  >
                    <option value="">Select location</option>
                    {locations.map((loc) => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.is_event}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setFormData({
                        ...formData,
                        is_event: on,
                        ...(on ? {} : { event_from: '', event_to: '' }),
                      });
                      if (!on) {
                        setEventThumbnailFile(null);
                        setEventBannerFile(null);
                      }
                    }}
                  />
                  Is Event
                </label>
                <p className="form-hint">Event tickets only appear when the customer’s visit date falls in the range below.</p>
              </div>
              {formData.is_event ? (
                <div className="event-ticket-fields">
                  <p className="event-fields-title">Event details</p>
                  <div className="event-date-column">
                    <div className="form-group">
                      <label>From date *</label>
                      <input
                        type="date"
                        value={formData.event_from}
                        onChange={(e) => setFormData({ ...formData, event_from: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>End date *</label>
                      <input
                        type="date"
                        value={formData.event_to}
                        min={formData.event_from || undefined}
                        onChange={(e) => setFormData({ ...formData, event_to: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Thumbnail image *</label>
                    <p className="form-dimensions-hint">
                      Recommended <strong>800 × 500 px</strong> (16∶10), JPG/PNG/WebP, max 2 MB.
                      Used on the customer ticket selection page.
                    </p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setEventThumbnailFile(e.target.files?.[0] || null)}
                    />
                    {editingTicket?.event_thumbnail && (
                      <p className="form-hint">
                        Current:{' '}
                        <a href={editingTicket.event_thumbnail} target="_blank" rel="noopener noreferrer">
                          view file
                        </a>
                        {' '}(upload a new file to replace)
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Banner image *</label>
                    <p className="form-dimensions-hint">
                      Recommended <strong>1200 × 400 px</strong> (3∶1 wide), JPG/PNG/WebP, max 2 MB.
                      Shown on the visit date page below opening hours.
                    </p>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(e) => setEventBannerFile(e.target.files?.[0] || null)}
                    />
                    {editingTicket?.event_banner && (
                      <p className="form-hint">
                        Current:{' '}
                        <a href={editingTicket.event_banner} target="_blank" rel="noopener noreferrer">
                          view file
                        </a>
                        {' '}(upload a new file to replace)
                      </p>
                    )}
                  </div>
                </div>
              ) : null}
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowModal(false); setEditingTicket(null); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">Save</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tickets;
